import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReactComponent as ChevronIcon } from 'assets/icons/chevron.svg';
import { clsx } from 'clsx';
import dayjs from 'dayjs';

import { isHighSeverityClimateFinding } from '../utils/monitoringResultsUtils';

import type IMonitoringClimateFindingCard from './interfaces/IMonitoringClimateFindingCard';

import styles from './sass/MonitoringClimateFindingCard.module.scss';

const formatLocation = (
  location?: { country?: string, region?: string | null, locality?: string | null },
): string => {
  if (!location) {
    return '';
  }

  return [location.locality, location.region, location.country]
    .filter((part): part is string => Boolean(part && String(part).trim()))
    .join(', ');
};

function MonitoringClimateFindingCard({
  finding,
  defaultExpanded = isHighSeverityClimateFinding(finding),
}: IMonitoringClimateFindingCard): React.JSX.Element {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState<boolean>(defaultExpanded);

  const formattedDate = dayjs(finding.observed_at).isValid()
    ? dayjs(finding.observed_at).format('MMM D, YYYY')
    : finding.observed_at;

  const themeLabel = t(`valueChainMap.monitoring.results.climateTheme.${finding.theme}`, {
    defaultValue: finding.theme,
  });
  const kindKey = finding.entity_kind || 'supplier';
  const locationLabel = formatLocation(finding.location);

  return (
    <article
      className={clsx(styles.container, {
        [styles.riskCritical]: finding.severity === 'critical',
        [styles.riskHigh]: finding.severity === 'high',
        [styles.riskMedium]: finding.severity === 'medium',
        [styles.riskLow]: finding.severity === 'low',
      })}
    >
      <button
        type="button"
        className={styles.header}
        onClick={() => setIsExpanded((prevExpanded) => !prevExpanded)}
        aria-expanded={isExpanded}
      >
        <div className={styles.headerMain}>
          {finding.source_url ? (
            <a
              href={finding.source_url}
              className={styles.titleLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(event) => event.stopPropagation()}
            >
              {finding.title}
            </a>
          ) : (
            <h3 className={styles.title}>{finding.title}</h3>
          )}

          <span className={styles.supplierName}>
            {finding.entity_name}
            {locationLabel ? ` · ${locationLabel}` : ''}
          </span>

          <div className={styles.metaRow}>
            <span>{formattedDate}</span>
            {finding.horizon ? (
              <>
                <span aria-hidden="true">·</span>
                <span>{finding.horizon}</span>
              </>
            ) : null}
            {finding.source_label ? (
              <>
                <span aria-hidden="true">·</span>
                <span>{finding.source_label}</span>
              </>
            ) : null}
          </div>

          <div className={styles.badgeRow}>
            <span className={clsx(styles.badge, styles.themeBadge)}>
              {t(`valueChainMap.monitoring.results.climateEntityKind.${kindKey}`, {
                defaultValue: kindKey,
              })}
            </span>
            <span className={clsx(styles.badge, styles.themeBadge)}>{themeLabel}</span>
            <span className={clsx(styles.badge, styles[`risk_${finding.severity}`])}>
              {t(`valueChainMap.monitoring.results.riskSeverity.${finding.severity}`)}
            </span>
            {typeof finding.confidence === 'number' ? (
              <span className={styles.confidence}>
                {t('valueChainMap.monitoring.results.confidence', {
                  value: Math.round(finding.confidence * 100),
                })}
              </span>
            ) : null}
          </div>
        </div>

        <ChevronIcon
          className={clsx(styles.chevron, { [styles.chevronExpanded]: isExpanded })}
          aria-hidden="true"
        />
      </button>

      {isExpanded ? (
        <div className={styles.body}>
          {finding.impact_trail?.length ? (
            <div className={styles.linkPathBox}>
              <span className={styles.linkPathLabel}>
                {t('valueChainMap.monitoring.results.climateImpactTrail')}
              </span>
              <div className={styles.linkPathTrail}>
                {finding.impact_trail.map((node, nodeIndex) => (
                  <React.Fragment key={`${finding.id}-${node.entity_name}-${node.role}`}>
                    <span className={styles.linkPathNode}>
                      <span className={styles.linkPathRole}>{node.role}</span>
                      <span className={styles.linkPathName}>{node.entity_name}</span>
                    </span>
                    {nodeIndex < finding.impact_trail!.length - 1 ? (
                      <span className={styles.linkPathArrow} aria-hidden="true">→</span>
                    ) : null}
                  </React.Fragment>
                ))}
              </div>
            </div>
          ) : null}

          {finding.shortage_risk?.summary ? (
            <p className={styles.mechanism}>
              <strong>
                {t('valueChainMap.monitoring.results.climateShortageRisk')}
                :
                {' '}
              </strong>
              {finding.shortage_risk.summary}
            </p>
          ) : null}

          {finding.recommended_action ? (
            <p className={styles.mechanism}>
              <strong>
                {t('valueChainMap.monitoring.results.climateRecommendedAction')}
                :
                {' '}
              </strong>
              {finding.recommended_action}
            </p>
          ) : null}

          {(finding.related_materials || []).length ? (
            <p className={styles.mechanism}>
              <strong>
                {t('valueChainMap.monitoring.results.climateMaterials')}
                :
                {' '}
              </strong>
              {finding.related_materials!.join(' · ')}
            </p>
          ) : null}
        </div>
      ) : null}
    </article>
  );
}

export default MonitoringClimateFindingCard;
